import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../Api';
import { TrashIcon, PlusIcon, ArrowLeftIcon, XMarkIcon, PencilIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Individual, EntitySummary, FamilySummary, FamilyDetails } from '../types/Family';
import ConfirmationModal from '../components/ConfirmationModal';
import CustomInput from '../controls/CustomInput';

const FamilyManagementDashboard: React.FC = () => {
    const { familyId } = useParams<{ familyId: string }>();
    const navigate = useNavigate();
    const familyIdNum = parseInt(familyId || '0');
    const api = useApi();
    const [families, setFamilies] = useState<FamilySummary[]>([]);
    const [selectedFamily, setSelectedFamily] = useState<FamilyDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [showAddMember, setShowAddMember] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<Individual[]>([]);
    const [searchAttempted, setSearchAttempted] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [isEditingKeys, setIsEditingKeys] = useState(false);
    const [editedFamilyName, setEditedFamilyName] = useState('');
    const [editedApiKeys, setEditedApiKeys] = useState({
        softLedgerApiKey: '',
        softLedgerApiSecret: '',
        softLedgerTenantId: '',
        billDotComOrganizationId: ''
    });
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
        apiKeys: true,
        familyMembers: true,
        businesses: true,
        trusts: true,
        bankAccounts: true,
        brokerageCustodians: true,
        charitableOrganizations: true,
        insuranceCompanies: true,
        vendors: true,
        contacts: true
    });
    const [confirmationModal, setConfirmationModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
        isLoading?: boolean;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        isLoading: false
    });

    const toggleSection = (sectionName: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionName]: !prev[sectionName]
        }));
    };

    // Format phone number for US/Canada (10 digits)
    const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
        if (!phoneNumber) return 'N/A';

        // Remove all non-digit characters
        const digits = phoneNumber.replace(/\D/g, '');

        // Check if it's a 10-digit US/Canada number
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }

        // Return original if not 10 digits
        return phoneNumber;
    };

    const loadFamilies = useCallback(async () => {
        try {
            const response = await api.get('/api/family/getfamilies');
            setFamilies(response.data.families || []);
        } catch (error: any) {
            console.error('Error loading families:', error);
            if (error.response?.status === 400) {
                console.error("Bad request:", error.response.data);
            }
        } finally {
            setLoading(false);
        }
    }, [api]);

    const loadFamilyDetails = useCallback(async () => {
        try {
            const response = await api.get(`/api/family/getfamilydetails?familyId=${familyIdNum}`);
            setSelectedFamily(response.data.family);
        } catch (error: any) {
            console.error('Error loading family details:', error);
            if (error.response?.status === 400) {
                console.error("Bad request:", error.response.data);
            }
        } finally {
            setLoading(false);
        }
    }, [api, familyIdNum]);

    useEffect(() => {
        if (familyIdNum) {
            loadFamilyDetails();
        } else {
            loadFamilies();
        }
    }, [familyIdNum, loadFamilies, loadFamilyDetails]);


    const searchIndividuals = async (term: string) => {
        setSearchAttempted(true);

        if (term.length < 2) {
            setSearchResults([]);
            return;
        }
        setSearchAttempted(false);
        setIsSearching(true);
        try {
            const response = await api.get(`/api/family/searchindividuals?searchTerm=${encodeURIComponent(term)}`);
            setSearchResults(response.data.individuals || []);
        } catch (error: any) {
            console.error('Error searching individuals:', error);
            if (error.response?.status === 400) {
                console.error("Bad request:", error.response.data);
            }
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const addIndividualToFamily = async (individualId: number) => {
        try {
            await api.post(`/api/family/addindividual?familyId=${familyIdNum}`,
                { individualId }
            );
            // Reload family details
            loadFamilyDetails();
            setShowAddMember(false);
            setSearchTerm('');
            setSearchResults([]);
        } catch (error: any) {
            console.error('Error adding individual to family:', error);
            if (error.response?.status === 400) {
                console.error("Bad request:", error.response.data);
            }
        }
    };

    const removeIndividualFromFamily = (individualId: number) => {
        const individual = selectedFamily?.individuals.find(i => i.id === individualId);
        const individualName = individual?.firstName + " " + individual?.lastName || 'this individual';
        setConfirmationModal({
            isOpen: true,
            title: 'Remove Individual',
            message: `Are you sure you want to remove **${individualName}** from this family? This will not delete the individual.`,
            onConfirm: () => confirmDeleteIndividualFromFamily(individualId),
            isLoading: false
        });

    };

    const confirmDeleteIndividualFromFamily = async (individualId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/family/removeindividual?familyId=${familyIdNum}&individualId=${individualId}`);
            loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error: any) {
            console.error('Error removing individual from family:', error);
            if (error.response?.status === 400) {
                console.error("Bad request:", error.response.data);
                setConfirmationModal(prev => ({ ...prev, isLoading: false }));
            }
        }
    };

    // Business Management Functions
    const handleAddBusiness = () => {
        navigate(`/family-management/${familyIdNum}/business/add`);
    };

    const handleEditBusiness = (business: EntitySummary) => {
        navigate(`/family-management/${familyIdNum}/business/edit?businessId=${business.id}`);
    };

    const handleDeleteBusiness = (businessId: number) => {
        const business = selectedFamily?.businesses.find(b => b.id === businessId);
        const businessName = business?.name || 'this business';

        setConfirmationModal({
            isOpen: true,
            title: 'Delete Business',
            message: `Are you sure you want to delete **${businessName}** ? This action cannot be undone.`,
            onConfirm: () => confirmDeleteBusiness(businessId),
            isLoading: false
        });
    };

    // Bank Account Management Functions
    const handleAddBankAccount = () => {
        navigate(`/family-management/${familyIdNum}/bankaccount/add`);
    };

    const handleEditBankAccount = (bankAccount: EntitySummary) => {
        navigate(`/family-management/${familyIdNum}/bankaccount/edit?bankAccountId=${bankAccount.id}`);
    };

    const handleDeleteBankAccount = (bankAccountId: number) => {
        const bankAccount = selectedFamily?.bankAccounts.find(b => b.id === bankAccountId);
        const bankAccountName = bankAccount?.name || 'this bank account';

        setConfirmationModal({
            isOpen: true,
            title: 'Delete Bank Account',
            message: `Are you sure you want to delete **${bankAccountName}** ? This action cannot be undone.`,
            onConfirm: () => confirmDeleteBankAccount(bankAccountId),
            isLoading: false
        });
    };

    const confirmDeleteBusiness = async (businessId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/business/removebusiness?businessId=${businessId}`);
            await loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
            console.error('Error deleting business:', error);
            setConfirmationModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    //Brokerage/Custodian Management Functions
    const handleAddBrokerageCustodian = () => {
        navigate(`/family-management/${familyIdNum}/brokerageCustodian/add`)
    };
    const handleEditBrokerageCustodian = (brokerageCustodian: EntitySummary) => {
        navigate(`/family-management/${familyIdNum}/brokerageCustodian/edit?brokerageCustodianId=${brokerageCustodian.id}`);
    };

    const handleDeleteBrokerageCustodian = (brokerageCustodianId: number) => {
        const brokerageCustodian = selectedFamily?.brokerageCustodians.find(t => t.id === brokerageCustodianId);
        const businessName = brokerageCustodian?.businessName || 'this brokerage/custodian';

        setConfirmationModal({
            isOpen: true,
            title: 'Delete Brokerage/Custodian',
            message: `Are you sure you want to delete **${businessName}** ? This action cannot be undone.`,
            onConfirm: () => confirmDeleteBrokerageCustodian(brokerageCustodianId),
            isLoading: false
        });
    };

    const confirmDeleteBrokerageCustodian = async (brokerageCustodianId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/brokerageCustodian/removebrokerageCustodian?brokerageCustodianId=${brokerageCustodianId}`);
            await loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
            console.error('Error deleting brokerageCustodian:', error);
            setConfirmationModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    // Trust Management Functions
    const handleAddTrust = () => {
        navigate(`/family-management/${familyIdNum}/trust/add`);
    };

    const handleEditTrust = (trust: EntitySummary) => {
        navigate(`/family-management/${familyIdNum}/trust/edit?trustId=${trust.id}`);
    };

    const handleDeleteTrust = (trustId: number) => {
        const trust = selectedFamily?.trusts.find(t => t.id === trustId);
        const trustName = trust?.name || 'this trust';

        setConfirmationModal({
            isOpen: true,
            title: 'Delete Trust',
            message: `Are you sure you want to delete **${trustName}** ? This action cannot be undone.`,
            onConfirm: () => confirmDeleteTrust(trustId),
            isLoading: false
        });
    };

    const confirmDeleteTrust = async (trustId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/trust/removetrust?trustId=${trustId}`);
            await loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
            console.error('Error deleting trust:', error);
            setConfirmationModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    // Charitable Organization Management Functions
    const handleAddCharitableOrganization = () => {
        navigate(`/family-management/${familyIdNum}/charitable-organization/add`);
    };

    const handleEditCharitableOrganization = (charitableOrganization: EntitySummary) => {
        navigate(`/family-management/${familyIdNum}/charitable-organization/edit?charitableOrganizationId=${charitableOrganization.id}`);
    };

    const handleDeleteCharitableOrganization = (charitableOrganizationId: number) => {
        const charitableOrganization = selectedFamily?.charitableOrganizations.find(co => co.id === charitableOrganizationId);
        const charitableOrganizationName = charitableOrganization?.name || 'this charitable organization';

        setConfirmationModal({
            isOpen: true,
            title: 'Delete Charitable Organization',
            message: `Are you sure you want to delete **${charitableOrganizationName}** ? This action cannot be undone.`,
            onConfirm: () => confirmDeleteCharitableOrganization(charitableOrganizationId),
            isLoading: false
        });
    };

    const confirmDeleteCharitableOrganization = async (charitableOrganizationId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/charitableorganization/removecharitableorganization?charitableOrganizationId=${charitableOrganizationId}`);
            await loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
            console.error('Error deleting charitable organization:', error);
            setConfirmationModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    const confirmDeleteBankAccount = async (bankAccountId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/bankaccount/removebankaccount?id=${bankAccountId}`);
            await loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
            console.error('Error deleting bank account:', error);
            setConfirmationModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    //Insurance Company Management Functions
    const handleAddInsuranceCompany = () => {
        navigate(`/family-management/${familyIdNum}/insurance-company/add`)
    };
    const handleEditInsuranceCompany = (insuranceCompany: EntitySummary) => {
        navigate(`/family-management/${familyIdNum}/insurance-company/edit?insuranceCompanyId=${insuranceCompany.id}`);
    };

    const handleDeleteInsuranceCompany = (insuranceCompanyId: number) => {
        const insuranceCompany = selectedFamily?.insuranceCompanies.find(ic => ic.id === insuranceCompanyId);
        const businessName = insuranceCompany?.businessName || 'this insurance company';

        setConfirmationModal({
            isOpen: true,
            title: 'Delete Insurance Company',
            message: `Are you sure you want to delete **${businessName}** ? This action cannot be undone.`,
            onConfirm: () => confirmDeleteInsuranceCompany(insuranceCompanyId),
            isLoading: false
        });
    };

    const confirmDeleteInsuranceCompany = async (insuranceCompanyId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/insurancecompany/removeinsurancecompany?insuranceCompanyId=${insuranceCompanyId}`);
            await loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
            console.error('Error deleting insurance company:', error);
            setConfirmationModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    // Vendor Management Functions
    const handleAddVendor = () => {
        navigate(`/family-management/${familyIdNum}/vendor/add`);
    };

    const handleEditVendor = (vendor: EntitySummary) => {
        navigate(`/family-management/${familyIdNum}/vendor/edit?vendorId=${vendor.id}`);
    };

    const handleDeleteVendor = (vendorId: number) => {
        const vendor = selectedFamily?.vendors.find(v => v.id === vendorId);
        const vendorName = vendor?.name || 'this vendor';

        setConfirmationModal({
            isOpen: true,
            title: 'Delete Vendor',
            message: `Are you sure you want to delete **${vendorName}** ? This action cannot be undone.`,
            onConfirm: () => confirmDeleteVendor(vendorId),
            isLoading: false
        });
    };

    const confirmDeleteVendor = async (vendorId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/vendor/removevendor?id=${vendorId}`);
            await loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
            console.error('Error deleting vendor:', error);
            setConfirmationModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    // Contact Management Functions
    const handleAddContact = () => {
        navigate(`/family-management/${familyIdNum}/contact/add`);
    };

    const handleEditContact = (contact: EntitySummary) => {
        navigate(`/family-management/${familyIdNum}/contact/edit?contactId=${contact.id}`);
    };

    const handleDeleteContact = (contactId: number) => {
        const contact = selectedFamily?.contacts.find(c => c.id === contactId);
        const contactName = contact?.name || 'this contact';

        setConfirmationModal({
            isOpen: true,
            title: 'Delete Contact',
            message: `Are you sure you want to delete **${contactName}** ? This action cannot be undone.`,
            onConfirm: () => confirmDeleteContact(contactId),
            isLoading: false
        });
    };

    const confirmDeleteContact = async (contactId: number) => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));

        try {
            await api.delete(`/api/contact/removecontact?id=${contactId}`);
            await loadFamilyDetails();
            setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
        } catch (error) {
            console.error('Error deleting contact:', error);
            setConfirmationModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value;
        setSearchTerm(term);
        // Clear results when user types (but don't search yet)
        if (term.length < 2) {
            setSearchResults([]);
        }
    };

    const editApiKeys = async () => {
        if (!selectedFamily) {
            return;
        }

        try {
            const response = await api.put(`/api/family/updatefamilykeys`, {
                familyId: familyIdNum,
                softLedgerApiKey: editedApiKeys.softLedgerApiKey,
                softLedgerApiSecret: editedApiKeys.softLedgerApiSecret,
                softLedgerTenantId: editedApiKeys.softLedgerTenantId,
                billDotComOrganizationId: editedApiKeys.billDotComOrganizationId
            });

            if (response?.data?.family) {
                setSelectedFamily(response.data.family);
                setIsEditingKeys(false);
            } else {
                alert("Failed to update API keys.");
            }
        } catch (error: any) {
            console.error("Error updating API keys:", error);
            alert("Failed to update API keys.");
        }
    };

    const editFamilyName = async () => {
        if (!selectedFamily || !editedFamilyName) {
            alert("Family name cannot be empty.");
            return;
        }

        try {
            const response = await api.put(`/api/family/updatefamilyname`, {
                id: familyIdNum,
                name: editedFamilyName
            });

            if (response?.data?.family) {
                setSelectedFamily(response.data.family);
                setIsEditingName(false);
            } else {
                alert("Failed to update family name.");
            }
        } catch (error: any) {
            console.error("Error updating family name:", error);
            alert("Failed to update family name.");
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    // Show family list if no specific family is selected
    if (!familyIdNum) {
        return (
            <div className="max-w-4xl mx-auto p-6 space-y-6">
                {/* Header Card */}
                <div className="bg-white rounded-xl shadow-md px-6 py-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Family Management</h1>
                    <p className="text-gray-600">
                        Select a family to manage members and linked entities
                    </p>
                </div>

                {/* Families Card */}
                <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                        Families {families.length > 0 && `(${families.length})`}
                    </h2>

                    {families.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500 text-lg">No families found</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {families.map((family) => (
                                <div
                                    key={family.id}
                                    onClick={() => navigate(`/family-management/${family.id}`)}
                                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                                >
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">{family.name}</h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                                        <div>
                                            <span className="font-medium">Members:</span>
                                            <div className="font-semibold text-gray-900">{family.memberCount}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium">Entities:</span>
                                            <div className="font-semibold text-gray-900">{family.entityCount}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium">Created:</span>
                                            <div>{new Date(family.createdAt).toLocaleDateString()}</div>
                                        </div>
                                        <div>
                                            <span className="font-medium">Updated:</span>
                                            <div>{new Date(family.updatedAt).toLocaleDateString()}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Show family detail view
    return (
        <div className="max-w-4xl mx-auto p-6 space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-xl shadow-md px-6 py-8">
                <div className="flex justify-between items-center mb-3">
                    <h1 className="text-3xl font-bold text-gray-900">
                        <div className="flex flex-col">
                            <div className="flex items-center space-x-2">
                                {isEditingName ? (
                                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                                        <CustomInput
                                            label="Family Name"
                                            name="familyName"
                                            value={editedFamilyName}
                                            onChange={setEditedFamilyName}
                                        />
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={editFamilyName}
                                                className="btn-primary h-10"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setIsEditingName(false)}
                                                className="btn-secondary h-10"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <h1 className="text-3xl font-bold">{selectedFamily?.name}</h1>
                                        <button onClick={() => {
                                            setEditedFamilyName(selectedFamily?.name || '');
                                            setIsEditingName(true);
                                        }}>
                                            <PencilIcon className="mt-2 h-5 w-5 text-gray-500 hover:text-gray-700" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </h1>
                    <button
                        onClick={() => navigate('/concierge-dashboard')}
                        className="btn-secondary"
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-2" />
                        Back
                    </button>
                </div>
                <p className="text-gray-600">
                    Manage family members and linked entities
                </p>
            </div>

            

            {selectedFamily ? (
                <>
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => toggleSection('apiKeys')}>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    External System Access Keys (SoftLedger, Bill.com)
                                </h2>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button onClick={(e) => {
                                    e.stopPropagation();
                                    setEditedApiKeys({
                                        ...editedApiKeys,
                                        softLedgerApiKey: selectedFamily?.softLedgerApiKey || '',
                                        softLedgerApiSecret: selectedFamily?.softLedgerApiSecret || '',
                                        softLedgerTenantId: selectedFamily?.softLedgerTenantId || '',
                                        billDotComOrganizationId: selectedFamily?.billDotComOrganizationId || ''
                                    });
                                    setIsEditingKeys(true);
                                    // Expand the section if it's collapsed
                                    if (collapsedSections.apiKeys) {
                                        setCollapsedSections(prev => ({ ...prev, apiKeys: false }));
                                    }
                                }}>
                                    <PencilIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                                </button>
                                <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.apiKeys ? '' : 'rotate-180' } cursor-pointer`} onClick={() => toggleSection('apiKeys')} />
                            </div>
                        </div>
                        {!collapsedSections.apiKeys && (
                            <div>
                                {!isEditingKeys && (
                                    <div className="space-y-4">
                                        <div className="bg-white rounded-xl shadow-md p-6">
                                             <div className="space-y-4">
                                                 <div>
                                                     <span className="font-medium">
                                                         SoftLedger API Key: &nbsp;
                                                     </span>
                                                     <div className="break-words">
                                                         {selectedFamily.softLedgerApiKey ?? 'Not Set'}
                                                     </div>
                                                 </div>

                                                  <div>
                                                     <span className="font-medium">
                                                         SoftLedger API Secret: &nbsp;
                                                     </span>
                                                     <div className="break-words">
                                                         {selectedFamily.softLedgerApiSecret ?? 'Not Set'}
                                                     </div>
                                                 </div>
                                                 
                                                 <div>
                                                     <span className="font-medium">
                                                         SoftLedger Tenant ID: 
                                                     </span>
                                                     <div className="break-words">
                                                         {selectedFamily.softLedgerTenantId ?? 'Not Set'}
                                                     </div>
                                                 </div>
                                                 <div>
                                                     <span className="font-medium">
                                                         Bill.com Organization ID: 
                                                     </span>
                                                     <div className="break-words">
                                                         {selectedFamily.billDotComOrganizationId ?? 'Not Set'}
                                                     </div>
                                                 </div>
                                             </div>
                                        </div>
                                    </div>
                                )}
                                {isEditingKeys && (
                                    <div className="space-y-4">
                                        <CustomInput
                                            label="SoftLedger API Key"
                                            name="softLedgerApiKey"
                                            value={editedApiKeys.softLedgerApiKey}
                                            onChange={(val) => setEditedApiKeys(prev => ({ ...prev, softLedgerApiKey: val }))}
                                        />
                                        <CustomInput
                                            label="SoftLedger API Secret"
                                            name="softLedgerApiSecret"
                                            value={editedApiKeys.softLedgerApiSecret}
                                            onChange={(val) => setEditedApiKeys(prev => ({ ...prev, softLedgerApiSecret: val }))}
                                        />
                                        <CustomInput
                                            label="SoftLedger Tenant ID"
                                            name="softLedgerTenantId"
                                            value={editedApiKeys.softLedgerTenantId}
                                            onChange={(val) => setEditedApiKeys(prev => ({ ...prev, softLedgerTenantId: val }))}
                                        />
                                        <CustomInput
                                            label="Bill.com Organization ID"
                                            name="billDotComOrganizationId"
                                            value={editedApiKeys.billDotComOrganizationId}
                                            onChange={(val) => setEditedApiKeys(prev => ({ ...prev, billDotComOrganizationId: val }))}
                                            maxLength={50}
                                        />
                                        <div className="mt-3 flex space-x-2">
                                            <button
                                                onClick={editApiKeys}
                                                className="btn-primary h-10"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setIsEditingKeys(false)}
                                                className="btn-secondary h-10"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        
                    </div>

                    {/* Family Members Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="flex items-center justify-between mb-6 cursor-pointer" onClick={() => toggleSection('familyMembers')}>
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Family Members
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.individuals.length}
                                </span>
                            </div>
                            <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.familyMembers ? '' : 'rotate-180'}`} />
                        </div>

                        {!collapsedSections.familyMembers && (
                            <>
                                {selectedFamily.individuals.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No members added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.individuals.map((individual) => (
                                            <div
                                                key={individual.id}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div>
                                                    <div className="flex justify-between items-center mb-3">
                                                        <h3 className="text-lg font-medium text-gray-900">
                                                            {individual.firstName} {individual.lastName}
                                                        </h3>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => removeIndividualFromFamily(individual.id)}
                                                                className="btn-danger btn-icon"
                                                                title="Remove member"
                                                            >
                                                                <TrashIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                        <div>
                                                            <span className="font-medium">Email:</span>
                                                            <div className="break-words">
                                                                {individual.email}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Phone:</span>
                                                            <div>{formatPhoneNumber(individual.phoneNumber)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={() => setShowAddMember(true)}
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Family Member</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Businesses Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div
                            className="flex items-center justify-between mb-6 cursor-pointer"
                            onClick={() => toggleSection('businesses')}
                        >
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Businesses
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.businesses.length}
                                </span>
                            </div>
                            <ChevronDownIcon
                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.businesses ? '' : 'rotate-180'
                                    }`}
                            />
                        </div>

                        {!collapsedSections.businesses && (
                            <>
                                {selectedFamily.businesses.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No businesses added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.businesses.map((entity) => (
                                            <div
                                                key={`business-${entity.id}`}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                            {entity.name}
                                                        </h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                            {entity.entityType && (
                                                                <div>
                                                                    <span className="font-medium">Type:</span>
                                                                    <div>{entity.entityType}</div>
                                                                </div>
                                                            )}
                                                            {entity.jurisdiction && (
                                                                <div>
                                                                    <span className="font-medium">Jurisdiction:</span>
                                                                    <div>{entity.jurisdiction}</div>
                                                                </div>
                                                            )}
                                                            {entity.primaryContact && (
                                                                <div>
                                                                    <span className="font-medium">Contact:</span>
                                                                    <div>{entity.primaryContact}</div>
                                                                </div>
                                                            )}
                                                            {entity.dateOfFormation && (
                                                                <div>
                                                                    <span className="font-medium">Formed:</span>
                                                                    <div>{new Date(entity.dateOfFormation).toLocaleDateString()}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => handleEditBusiness(entity)}
                                                            className="btn-secondary btn-icon"
                                                            title="Edit business"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteBusiness(entity.id)}
                                                            className="btn-danger btn-icon"
                                                            title="Delete business"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={handleAddBusiness}
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Business</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Trusts Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div
                            className="flex items-center justify-between mb-6 cursor-pointer"
                            onClick={() => toggleSection('trusts')}
                        >
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Trusts
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.trusts.length}
                                </span>
                            </div>
                            <ChevronDownIcon
                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.trusts ? '' : 'rotate-180'
                                    }`}
                            />
                        </div>

                        {!collapsedSections.trusts && (
                            <>
                                {selectedFamily.trusts.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No trusts added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.trusts.map((entity) => (
                                            <div
                                                key={`trust-${entity.id}`}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                            {entity.name}
                                                        </h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                            {entity.entityType && (
                                                                <div>
                                                                    <span className="font-medium">Type:</span>
                                                                    <div>{entity.entityType}</div>
                                                                </div>
                                                            )}
                                                            {entity.jurisdiction && (
                                                                <div>
                                                                    <span className="font-medium">Jurisdiction:</span>
                                                                    <div>{entity.jurisdiction}</div>
                                                                </div>
                                                            )}
                                                            {entity.dateOfFormation && (
                                                                <div>
                                                                    <span className="font-medium">Formed:</span>
                                                                    <div>{new Date(entity.dateOfFormation).toLocaleDateString()}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => handleEditTrust(entity)}
                                                            className="btn-secondary btn-icon"
                                                            title="Edit trust"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteTrust(entity.id)}
                                                            className="btn-danger btn-icon"
                                                            title="Delete trust"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={handleAddTrust}
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Trust</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Bank Accounts Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div
                            className="flex items-center justify-between mb-6 cursor-pointer"
                            onClick={() => toggleSection('bankAccounts')}
                        >
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Bank Accounts
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.bankAccounts.length}
                                </span>
                            </div>
                            <ChevronDownIcon
                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.bankAccounts ? '' : 'rotate-180'
                                    }`}
                            />
                        </div>

                        {!collapsedSections.bankAccounts && (
                            <>
                                {selectedFamily.bankAccounts.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No bank accounts added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.bankAccounts.map((entity) => (
                                            <div
                                                key={`bankaccount-${entity.id}`}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                            {entity.name}
                                                        </h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                            {entity.accountType && (
                                                                <div>
                                                                    <span className="font-medium">Type:</span>
                                                                    <div>{entity.accountType}</div>
                                                                </div>
                                                            )}
                                                            {entity.glCode && (
                                                                <div>
                                                                    <span className="font-medium">GL Code:</span>
                                                                    <div>{entity.glCode}</div>
                                                                </div>
                                                            )}
                                                            {entity.assetsType && (
                                                                <div>
                                                                    <span className="font-medium">Assets Type:</span>
                                                                    <div>{entity.assetsType}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => handleEditBankAccount(entity)}
                                                            className="btn-secondary btn-icon"
                                                            title="Edit bank account"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteBankAccount(entity.id)}
                                                            className="btn-danger btn-icon"
                                                            title="Delete bank account"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={() => {
                                            handleAddBankAccount();
                                        }}
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Bank Account</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Brokerage Custodians Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div
                            className="flex items-center justify-between mb-6 cursor-pointer"
                            onClick={() => toggleSection('brokerageCustodians')}
                        >
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Brokerage Custodians
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.brokerageCustodians.length}
                                </span>
                            </div>
                            <ChevronDownIcon
                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.brokerageCustodians ? '' : 'rotate-180'
                                    }`}
                            />
                        </div>

                        {!collapsedSections.brokerageCustodians && (
                            <>
                                {selectedFamily.brokerageCustodians.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No brokerage custodians added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.brokerageCustodians.map((entity) => (
                                            <div
                                                key={`brokerage-${entity.id}`}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                            {entity.name}
                                                        </h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                            {entity.businessType && (
                                                                <div>
                                                                    <span className="font-medium">Type:</span>
                                                                    <div>{entity.businessType}</div>
                                                                </div>
                                                            )}
                                                            {entity.assetsClass && (
                                                                <div>
                                                                    <span className="font-medium">Assets Class:</span>
                                                                    <div>{entity.assetsClass}</div>
                                                                </div>
                                                            )}
                                                            {entity.businessEmail && (
                                                                <div>
                                                                    <span className="font-medium">Email:</span>
                                                                    <div>{entity.businessEmail}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => {
                                                                handleEditBrokerageCustodian(entity)
                                                            }}
                                                            className="btn-secondary btn-icon"
                                                            title="Edit brokerage custodian"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                handleDeleteBrokerageCustodian(entity.id)
                                                            }}
                                                            className="btn-danger btn-icon"
                                                            title="Delete brokerage custodian"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={
                                            handleAddBrokerageCustodian
                                        }
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Brokerage Custodian</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Charitable Organizations Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div
                            className="flex items-center justify-between mb-6 cursor-pointer"
                            onClick={() => toggleSection('charitableOrganizations')}
                        >
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Charitable Organizations
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.charitableOrganizations.length}
                                </span>
                            </div>
                            <ChevronDownIcon
                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.charitableOrganizations ? '' : 'rotate-180'
                                    }`}
                            />
                        </div>

                        {!collapsedSections.charitableOrganizations && (
                            <>
                                {selectedFamily.charitableOrganizations.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No charitable organizations added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.charitableOrganizations.map((entity) => (
                                            <div
                                                key={`charitable-${entity.id}`}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                            {entity.name}
                                                        </h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                            {entity.entityType && (
                                                                <div>
                                                                    <span className="font-medium">Type:</span>
                                                                    <div>{entity.entityType}</div>
                                                                </div>
                                                            )}
                                                            {entity.jurisdiction && (
                                                                <div>
                                                                    <span className="font-medium">Jurisdiction:</span>
                                                                    <div>{entity.jurisdiction}</div>
                                                                </div>
                                                            )}
                                                            {entity.dateOfFormation && (
                                                                <div>
                                                                    <span className="font-medium">Formed:</span>
                                                                    <div>{new Date(entity.dateOfFormation).toLocaleDateString()}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => handleEditCharitableOrganization(entity)}
                                                            className="btn-secondary btn-icon"
                                                            title="Edit charitable organization"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCharitableOrganization(entity.id)}
                                                            className="btn-danger btn-icon"
                                                            title="Delete charitable organization"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={handleAddCharitableOrganization}
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Charitable Organization</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Insurance Companies Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div
                            className="flex items-center justify-between mb-6 cursor-pointer"
                            onClick={() => toggleSection('insuranceCompanies')}
                        >
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Insurance Companies
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.insuranceCompanies.length}
                                </span>
                            </div>
                            <ChevronDownIcon
                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.insuranceCompanies ? '' : 'rotate-180'
                                    }`}
                            />
                        </div>

                        {!collapsedSections.insuranceCompanies && (
                            <>
                                {selectedFamily.insuranceCompanies.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No insurance companies added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.insuranceCompanies.map((entity) => (
                                            <div
                                                key={`insurance-${entity.id}`}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                            {entity.name}
                                                        </h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                            {entity.insuranceType && (
                                                                <div>
                                                                    <span className="font-medium">Insurance Type:</span>
                                                                    <div>{entity.insuranceType}</div>
                                                                </div>
                                                            )}
                                                            {entity.businessEmail && (
                                                                <div>
                                                                    <span className="font-medium">Email:</span>
                                                                    <div>{entity.businessEmail}</div>
                                                                </div>
                                                            )}
                                                            {entity.businessPhone && (
                                                                <div>
                                                                    <span className="font-medium">Phone:</span>
                                                                    <div>{formatPhoneNumber(entity.businessPhone)}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => {
                                                                handleEditInsuranceCompany(entity)
                                                            }}
                                                            className="btn-secondary btn-icon"
                                                            title="Edit insurance company"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                handleDeleteInsuranceCompany(entity.id)
                                                            }}
                                                            className="btn-danger btn-icon"
                                                            title="Delete insurance company"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={
                                            handleAddInsuranceCompany
                                        }
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Insurance Company</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Vendors Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div
                            className="flex items-center justify-between mb-6 cursor-pointer"
                            onClick={() => toggleSection('vendors')}
                        >
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Vendors
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.vendors.length}
                                </span>
                            </div>
                            <ChevronDownIcon
                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.vendors ? '' : 'rotate-180'
                                    }`}
                            />
                        </div>

                        {!collapsedSections.vendors && (
                            <>
                                {selectedFamily.vendors.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No vendors added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.vendors.map((entity) => (
                                            <div
                                                key={`vendor-${entity.id}`}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                            {entity.name}
                                                        </h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                            {entity.vendorId && (
                                                                <div>
                                                                    <span className="font-medium">Vendor ID:</span>
                                                                    <div>{entity.vendorId}</div>
                                                                </div>
                                                            )}
                                                            {entity.primaryEmail && (
                                                                <div>
                                                                    <span className="font-medium">Email:</span>
                                                                    <div>{entity.primaryEmail}</div>
                                                                </div>
                                                            )}
                                                            {entity.phone && (
                                                                <div>
                                                                    <span className="font-medium">Phone:</span>
                                                                    <div>{formatPhoneNumber(entity.phone)}</div>
                                                                </div>
                                                            )}
                                                            {entity.is1099 !== undefined && (
                                                                <div>
                                                                    <span className="font-medium">1099:</span>
                                                                    <div>{entity.is1099 ? 'Yes' : 'No'}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => handleEditVendor(entity)}
                                                            className="btn-secondary btn-icon"
                                                            title="Edit vendor"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteVendor(entity.id)}
                                                            className="btn-danger btn-icon"
                                                            title="Delete vendor"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={handleAddVendor}
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Vendor</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Contact Card */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div
                            className="flex items-center justify-between mb-6 cursor-pointer"
                            onClick={() => toggleSection('contacts')}
                        >
                            <div className="flex items-center space-x-3">
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Contacts
                                </h2>
                                <span className="bg-gradient-to-r from-gray-500 to-gray-600 text-white text-sm font-medium px-2.5 py-0.5 rounded-full">
                                    {selectedFamily.contacts.length}
                                </span>
                            </div>
                            <ChevronDownIcon
                                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${collapsedSections.contacts ? '' : 'rotate-180'
                                    }`}
                            />
                        </div>

                        {!collapsedSections.contacts && (
                            <>
                                {selectedFamily.contacts.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-lg">No contacts added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {selectedFamily.contacts.map((entity) => (
                                            <div
                                                key={`contact-${entity.id}`}
                                                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors shadow-sm hover:shadow-md"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                                            {entity.name}
                                                        </h3>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                                                            {entity.contactType && (
                                                                <div>
                                                                    <span className="font-medium">Contact Type:</span>
                                                                    <div>{entity.contactType}</div>
                                                                </div>
                                                            )}
                                                            {entity.email && (
                                                                <div>
                                                                    <span className="font-medium">Email:</span>
                                                                    <div>{entity.email}</div>
                                                                </div>
                                                            )}
                                                            {entity.phoneNumber && (
                                                                <div>
                                                                    <span className="font-medium">Phone:</span>
                                                                    <div>{formatPhoneNumber(entity.phoneNumber)}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="ml-4 flex space-x-2">
                                                        <button
                                                            onClick={() => {
                                                                handleEditContact(entity)
                                                            }}
                                                            className="btn-secondary btn-icon"
                                                            title="Edit contact"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                handleDeleteContact(entity.id)
                                                            }}
                                                            className="btn-danger btn-icon"
                                                            title="Delete contact"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-center mt-6">
                                    <button
                                        onClick={
                                            handleAddContact
                                        }
                                        className="btn-primary"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="mr-1">Add Contact</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </>
            ) : (
                <div className="bg-white rounded-xl shadow-md p-6">
                    <div className="text-center py-8">
                        <p className="text-gray-500 text-lg">Loading family details...</p>
                    </div>
                </div>
            )}

            {/* Add Family Member Modal */}
            {showAddMember && selectedFamily && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-md p-6 w-96 max-w-md mx-4 max-h-[80vh] overflow-y-auto relative">
                        {/* Close Button */}
                        <button
                            onClick={() => {
                                setShowAddMember(false);
                                setSearchTerm('');
                                setSearchResults([]);
                            }}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Close"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>

                        <h3 className="text-lg font-medium text-gray-900 mb-4 pr-8">Add Member to {selectedFamily.name}</h3>

                        {/* Search Bar */}
                        <div className="mb-4">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            searchIndividuals(searchTerm);
                                        }
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <button
                                    onClick={() => searchIndividuals(searchTerm)}
                                    disabled={!searchTerm.trim() || isSearching}
                                    className="btn-secondary px-4 py-2 disabled:cursor-not-allowed"
                                >
                                    {isSearching ? 'Searching...' : 'Search'}
                                </button>
                            </div>
                        </div>

                        {/* Search Results */}
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {searchAttempted && searchTerm.length < 2 && (
                                <div className="text-center text-red-500 py-4">
                                    Please enter at least 2 characters to search.
                                </div>
                            )}

                            {searchResults.length > 0 ? (
                                searchResults.map((individual) => (
                                    <div key={individual.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                        <div>
                                            <div className="font-medium text-gray-900">
                                                {individual.firstName} {individual.lastName}
                                            </div>
                                            <div className="text-sm text-gray-500">{individual.email}</div>
                                            {individual.phoneNumber && (
                                                <div className="text-sm text-gray-500">{formatPhoneNumber(individual.phoneNumber)}</div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => addIndividualToFamily(individual.id)}
                                            className="btn-primary px-3 py-1 text-sm"
                                        >
                                            Add
                                        </button>
                                    </div>
                                ))

                            ) : (
                                (!searchAttempted) && (
                                    <div className="text-center text-gray-500 py-4">
                                        Click search to view results
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmationModal.isOpen}
                onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmationModal.onConfirm}
                title={confirmationModal.title}
                message={confirmationModal.message}
                confirmText="Delete"
                cancelText="Cancel"
                isDestructive={true}
                isLoading={confirmationModal.isLoading}
            />

        </div>
    );
};

export default FamilyManagementDashboard;

